import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { Subject, debounceTime } from 'rxjs';

import { IconComponent } from '@atoms/icon/icon.component';
import { InputErrorComponent } from '@atoms/input-error/input-error.component';
import { TooltipDirective } from '@directives/tooltip.directive';
import { ThousandsPipe } from '@pipes/thousands.pipe';

@Component({
  selector: 'app-input',
  imports: [NgClass, FormsModule, MatNativeDateModule, MatDatepickerModule, IconComponent, InputErrorComponent, TooltipDirective],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputComponent),
    },
  ],
})
export class InputComponent implements ControlValueAccessor, OnInit, OnChanges {
  private thousandsPipe = inject(ThousandsPipe);
  @Input() value?: string | number;
  @Input() name: string = '';
  @Input() id: string = '';
  @Input() label?: string;
  @Input() icon?: string;
  @Input() size: 'medium' | 'small' | 'large' = 'medium';
  @Input() type: 'text' | 'number' | 'email' | 'password' | 'date' = 'text';
  @Input() pattern?: string;
  @Input() min?: number;
  @Input() max?: number;
  @Input() thousands?: boolean;
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() disabled: boolean = false;
  @Input() autocomplete: 'on' | 'off' = 'off';
  @Input() tooltip?: string;
  @Input() error: boolean = false;
  @Input('error-message') errorMessage?: string;
  @Input() regex?: any;
  @Input() after?: string;
  @Input('text-align') textAlign: 'left' | 'center' | 'right' = 'left';
  @Output() updated: EventEmitter<string | number> = new EventEmitter<string | number>();
  @Output() valueChange: EventEmitter<string | number> = new EventEmitter<string | number>();
  @Output() validChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  public valueUpdate = new Subject<string | number | undefined>();
  public valueLength: number = 0;
  public visiblePassword: boolean = false;
  public valueType?: 'string' | 'number';
  public valid?: boolean;
  public out: boolean = false;
  public afterInitialized: boolean = false;
  public actualDate: Date = new Date();
  public onChange = (value: string) => {};
  public onTouched = (value: boolean) => {};

  focusOut(): void {
    this.out = true;
    this.validate(true, true);
  }

  formatThousands(): void {
    if (!this.thousands) return;
    const value = this.thousandsPipe.transform(this.value);
    if (value) this.value = value;
  }

  checkMinMax(): void {
    if (typeof this.value !== 'number') this.value = Number(this.value?.replace(/\u00A0/g, ''));
    if (this.min) this.value = this.value < this.min ? this.min : this.value;
    if (this.max) this.value = this.value > this.max ? this.max : this.value;
    this.value = this.value.toString();
    this.formatThousands();
  }

  keydown(event: KeyboardEvent): void {
    if (this.type === 'number' && event.key === 'e') event.preventDefault();
  }

  validate(final?: boolean, init?: boolean): void {
    if (this.disabled) return;
    this.onTouched(true);

    this.valueLength = this.value?.toString().length ?? 0;
    if (this.value === undefined) return;
    if (final && (this.min || this.max)) this.checkMinMax();

    if (this.valueType === 'number' && this.value !== 'number') this.value = Number(this.value);
    let regexResult: boolean = false;
    if (this.regex) regexResult = this.regex.test(this.value);

    if (typeof this.value === 'number') this.value = this.value.toString();
    if (typeof this.value === 'string') this.valid = (this.required && !this.value.length) || (this.regex && !regexResult) ? false : true;

    const value = this.thousands ? Number(this.value.replace(/\u00A0/g, '')) : this.value;
    this.onChange(value as string);
    if (init) return;
    this.valueUpdate.next(value as string | number);
    this.validChange.emit(this.valid);
    this.valueChange.emit(value);
  }

  togglePassword(): void {
    this.visiblePassword = !this.visiblePassword;
  }

  constructor() {
    this.valueUpdate.pipe(debounceTime(1000), takeUntilDestroyed()).subscribe((value: string | number | undefined) => {
      this.updated.emit(value);
    });
  }

  writeValue(obj: string | number): void {
    this.value = obj;
    this.validate(undefined, true);
    this.formatThousands();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: (value: boolean) => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnInit(): void {
    if (this.type === 'number' && typeof this.value === 'string') this.value = Number(this.value);
    if (this.thousands) this.valueType = 'string';
    else this.valueType = typeof this.value === 'number' ? 'number' : 'string';

    if (this.id === '' && this.name) this.id = 'form-id-' + this.name;
    this.formatThousands();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] !== undefined && changes['value'] !== null && !changes['value'].firstChange) this.formatThousands();
  }
}
